import { cardSurfaceClass } from "@/components/threads/shared.tsx";
import { cn } from "@/lib/utils.ts";
import { useCallback, useEffect, useRef, useState } from "react";
import type { RoutineSettingDef, RoutineSettingValues } from "../../types/routines.ts";
import { Checkbox } from "../ui/checkbox.tsx";
import { Input } from "../ui/input.tsx";
import { Label } from "../ui/label.tsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select.tsx";
import { Textarea } from "../ui/textarea.tsx";
import { errorToast } from "../ui/toast.tsx";
import { Toggle } from "../ui/toggle.tsx";

/**
 * Settings tab for a system routine. Renders one control per
 * declared setting (toggle / select / text) and writes each change instantly
 * (no Save button) — mirroring the Enabled toggle's write-through.
 *
 * Controlled leaf: the actual persistence is injected via `onWrite`, so the
 * same panel serves both the user-facing detail view (which writes to
 * `/api/a/routines/...`) and the admin editor (which writes to a target account's
 * `/api/s/routines/:accountId/...`). The parent refetches on success, which
 * re-seeds `values`.
 */
export function RoutineSettingsPanel({
  settings,
  values,
  onWrite,
}: {
  settings: RoutineSettingDef[];
  values: RoutineSettingValues;
  onWrite: (patch: RoutineSettingValues) => Promise<void>;
}) {
  const [local, setLocal] = useState<RoutineSettingValues>(values);

  // Latest server-confirmed values. Used to revert a single failed write
  // without closing over a stale snapshot or clobbering other in-flight edits.
  const valuesRef = useRef(values);

  // Mirror of the latest local values, updated synchronously so a read-modify-
  // write toggle (multi-select channels) composes against the newest state
  // rather than a stale render-time snapshot — two rapid toggles won't clobber
  // each other. Kept in sync with `local` on every render (revert / reseed).
  const localRef = useRef(local);
  localRef.current = local;

  // Serializes persistence so overlapping edits land in the order they were made,
  // not in network-completion order. Each write chains onto the previous one, so
  // two rapid toggles can't have their independent PUTs reorder on the wire (the
  // last-composed value would otherwise be clobbered by an earlier PUT arriving
  // last), and same-blob server merges can't race across keys either.
  const writeChainRef = useRef<Promise<void>>(Promise.resolve());

  // Re-seed from the server whenever the routine reloads (e.g. after a write
  // invalidates the query, or when switching routines).
  useEffect(() => {
    valuesRef.current = values;
    setLocal(values);
  }, [values]);

  const write = useCallback(
    (key: string, value: string | boolean | string[]) => {
      setLocal((prev) => ({ ...prev, [key]: value }));
      const run = async () => {
        try {
          await onWrite({ [key]: value });
        } catch {
          errorToast("Failed to update setting");
          // Revert only this key to the latest server value; leave any other
          // in-flight optimistic edits untouched.
          setLocal((prev) => ({ ...prev, [key]: valuesRef.current[key] }));
        }
      };
      // `run` never rejects (it catches internally), so the chain never breaks.
      const next = writeChainRef.current.then(run);
      writeChainRef.current = next;
      return next;
    },
    [onWrite],
  );

  // Add or remove one item from a string[] setting, composing against the latest
  // value (via localRef) so overlapping toggles don't overwrite one another.
  const toggleArrayItem = useCallback(
    (key: string, item: string, on: boolean) => {
      const current = localRef.current[key];
      const cur = Array.isArray(current)
        ? current.filter((v): v is string => typeof v === "string")
        : [];
      const next = on ? [...cur.filter((c) => c !== item), item] : cur.filter((c) => c !== item);
      localRef.current = { ...localRef.current, [key]: next };
      void write(key, next);
    },
    [write],
  );

  return (
    <div className="flex flex-col gap-2">
      {settings.map((def) => (
        <SettingRow
          key={def.key}
          def={def}
          value={local[def.key]}
          onWrite={write}
          onToggleArrayItem={toggleArrayItem}
        />
      ))}
    </div>
  );
}

function SettingRow({
  def,
  value,
  onWrite,
  onToggleArrayItem,
}: {
  def: RoutineSettingDef;
  value: RoutineSettingValues[string] | undefined;
  onWrite: (key: string, value: string | boolean | string[]) => void;
  onToggleArrayItem: (key: string, item: string, on: boolean) => void;
}) {
  // labelList and multiSelect settings render through the dedicated label
  // editors (the Auto Inbox catalog, the digest selector), not this panel.
  if (def.type === "labelList" || def.type === "multiSelect") {
    return null;
  }

  if (def.type === "notificationChannels") {
    // Options are the user's connected channels, resolved server-side. The value
    // is the subset of channel keys the user has picked.
    const selected = Array.isArray(value)
      ? value.filter((v): v is string => typeof v === "string")
      : [];
    const connected = def.options ?? [];
    const connectedValues = new Set(connected.map((o) => o.value));
    // Surface any still-selected channel that is no longer connected, so the user
    // can always uncheck a stale selection (otherwise a disconnected channel
    // stays in the saved value with no control to remove it, and delivery can
    // silently fail). Rendered after the connected ones, flagged as disconnected.
    const staleSelected = selected
      .filter((v) => !connectedValues.has(v))
      .map((v) => ({ value: v, label: `${v} (not connected)` }));
    const rows = [...connected, ...staleSelected];
    // Composes against the latest value (not this render's `selected`), so two
    // overlapping toggles don't clobber each other.
    const toggleChannel = (channel: string, on: boolean) => onToggleArrayItem(def.key, channel, on);
    return (
      <div className={cn(cardSurfaceClass, "w-full")}>
        <SettingLabel def={def} />
        <div className="mt-2 flex flex-col gap-2">
          {rows.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              No channels connected yet. Connect a channel in Settings to deliver here.
            </div>
          ) : (
            rows.map((opt) => {
              const id = `notify-channel-${def.key}-${opt.value}`;
              return (
                <label
                  key={opt.value}
                  htmlFor={id}
                  className="flex items-center gap-2 text-sm cursor-pointer select-none"
                >
                  <Checkbox
                    id={id}
                    checked={selected.includes(opt.value)}
                    onCheckedChange={(checked) => toggleChannel(opt.value, checked === true)}
                  />
                  {opt.label}
                </label>
              );
            })
          )}
        </div>
      </div>
    );
  }

  if (def.type === "toggle") {
    return (
      <div className={cn(cardSurfaceClass, "flex w-full items-center justify-between")}>
        <SettingLabel def={def} />
        <Toggle checked={value === true} onCheckedChange={(next) => onWrite(def.key, next)} />
      </div>
    );
  }

  if (def.type === "select") {
    return (
      <div className={cn(cardSurfaceClass, "flex w-full items-center justify-between gap-3")}>
        <SettingLabel def={def} />
        <Select
          value={typeof value === "string" ? value : def.default}
          onValueChange={(next) => onWrite(def.key, next)}
        >
          <SelectTrigger className="h-8 text-sm w-40 shrink-0 rounded-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {def.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <TextSettingRow def={def} value={typeof value === "string" ? value : ""} onWrite={onWrite} />
  );
}

// Text settings write on blur (not on every keystroke) to keep the instant
// write-through to one request per edit.
function TextSettingRow({
  def,
  value,
  onWrite,
}: {
  def: Extract<RoutineSettingDef, { type: "text" }>;
  value: string;
  onWrite: (key: string, value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = useCallback(() => {
    if (draft !== value) onWrite(def.key, draft);
  }, [draft, value, def.key, onWrite]);

  return (
    <div className={cn(cardSurfaceClass, "w-full")}>
      <SettingLabel def={def} />
      <div className="mt-2">
        {def.multiline ? (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            placeholder={def.placeholder}
            className="text-sm min-h-[60px]"
          />
        ) : (
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            placeholder={def.placeholder}
            className="h-8 text-sm"
          />
        )}
      </div>
    </div>
  );
}

function SettingLabel({ def }: { def: RoutineSettingDef }) {
  return (
    <div className="text-left min-w-0">
      <Label>{def.label}</Label>
      {def.description && (
        <div className="text-xs text-muted-foreground mt-0.5">{def.description}</div>
      )}
    </div>
  );
}
